/**
 * Small display primitives shared by player, faculty and projector views.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ClockView, Colour, Role } from '@nightshift/engine/client';

export const ROLE_TITLE: Record<Role, string> = { EM: 'Emergency', SURG: 'Surgeon', PHYS: 'Physician', PSYCH: 'Psychiatrist', ICU: 'Intensivist' };
export const ROLE_SHORT: Record<Role, string> = { EM: 'Em', SURG: 'Surg', PHYS: 'Phys', PSYCH: 'Psych', ICU: 'ICU' };
export const RESOURCE_TITLE = { LOCUM: 'Locum', THEATRE: 'Second Theatre', BED_MEETING: 'Bed meeting' } as const;

export function TriageBadge({ colour, size = 30 }: { colour: Colour; size?: number }) {
  const letter = colour[0];
  const fill = colour === 'GREEN' ? 'var(--green)' : colour === 'AMBER' ? 'var(--amber)' : 'var(--red)';
  const label = colour === 'GREEN' ? 'Green' : colour === 'AMBER' ? 'Amber' : 'Red';
  return (
    <span className={`triage ${colour}`} style={{ width: size, height: size }} role="img" aria-label={`${label} triage`}>
      <svg viewBox="0 0 30 30" width={size} height={size} aria-hidden>
        {colour === 'GREEN' && <circle cx="15" cy="15" r="13" fill={fill} />}
        {colour === 'AMBER' && <path d="M15 2 L28 26 L2 26 Z" fill={fill} strokeLinejoin="round" />}
        {colour === 'RED' && <path d="M15 1 L29 15 L15 29 L1 15 Z" fill={fill} />}
      </svg>
      <span style={colour === 'AMBER' ? { marginTop: 6 } : undefined}>{letter}</span>
    </span>
  );
}

export function ClockRow({ ticks, len, color }: { ticks: number; len: number; color?: string }) {
  return (
    <span className="clockrow" style={{ color: color ?? 'var(--ink)' }} role="img" aria-label={`Waiting time: ${ticks} of ${len} used`}>
      {Array.from({ length: len }, (_, i) => (
        <i key={i} className={`${i < ticks ? 'on' : ''} ${i === len - 1 ? 'last' : ''}`} />
      ))}
    </span>
  );
}

const BOXES = [50, 70, 90, 100] as const;

/** The certainty track with its paperclip on the current box. */
export function CertaintyTrack({ box, printed }: { box: number; printed: number }) {
  return (
    <span className="cert" role="img" aria-label={`Certainty ${box === 100 ? 'confirmed' : `${box} percent`}`}>
      {BOXES.map((b) => (
        <b key={b} className={`${b === box ? 'at' : ''} ${b === 100 ? 'ok' : ''} ${b === printed ? 'printed' : ''}`}>
          {b === 100 ? '✓' : b}
          {b === box && (
            <svg className="clip" viewBox="0 0 14 30" aria-hidden>
              <path d="M4 26 V6 a3 3 0 0 1 6 0 V22 a2 2 0 0 1 -4 0 V9" fill="none" stroke="#7a8098" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          )}
        </b>
      ))}
    </span>
  );
}

export function Pips({ available, spent }: { available: number; spent: number }) {
  const total = available + spent;
  return (
    <span className="pips" role="img" aria-label={`${available} action${available === 1 ? '' : 's'} left of ${total}`}>
      {Array.from({ length: Math.max(total, 0) }, (_, i) => (
        <i key={i} className={i < available ? 'on' : ''} />
      ))}
    </span>
  );
}

/** Countdown ring and m:ss text. `now` is server-corrected time. */
export function Countdown({ endsAt, remainingMs, paused, now, total }: { endsAt: number | null; remainingMs: number | null; paused: boolean; now: number; total?: number }) {
  const left = paused ? (remainingMs ?? 0) : endsAt ? Math.max(0, endsAt - now) : 0;
  const s = Math.ceil(left / 1000);
  const p = total ? Math.max(0, Math.min(1, left / total)) : 1;
  const [pulse, setPulse] = useState(false);
  const prev = useRef(s);
  useEffect(() => {
    if ((prev.current > 30 && s <= 30) || (prev.current > 10 && s <= 10)) {
      setPulse(true);
      if ('vibrate' in navigator) navigator.vibrate?.(60);
      window.setTimeout(() => setPulse(false), 1300);
    }
    prev.current = s;
  }, [s]);
  if (!endsAt && !paused) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} aria-live="off">
      <span className={`ring ${pulse ? 'pulse' : ''}`} style={{ ['--p' as string]: p, ['--ring' as string]: s <= 10 ? 'var(--red)' : 'var(--lamp)' }} />
      <span className="mono" style={{ fontSize: 'var(--t-16)', minWidth: 42 }}>
        {paused ? 'paused' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`}
      </span>
    </span>
  );
}

export function Sheet({ open, onClose, children, label }: { open: boolean; onClose: () => void; children: ReactNode; label: string }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={label}>
        <div className="grab" />
        {children}
      </div>
    </>
  );
}

const PHASE_NAMES: Record<string, string> = {
  SETUP: 'Lobby', ARRIVALS: 'Arrivals', HUDDLE: 'Huddle', ACT: 'Action', TICK_OPEN: 'Results', TICK_CLOSE: 'Results', LOG: 'Review', RELEASE: 'Next turn', HANDOVER: 'Handover', DEBRIEF: 'Debrief', ENDED: 'Ended',
};
export function phaseName(p: string): string {
  return PHASE_NAMES[p] ?? p;
}

const PHASE_HELP: Record<ClockView['phase'] | 'RELEASE', string> = {
  SETUP: 'Control starts the shift when everyone is seated. Each turn then starts with the Action phase.',
  ARRIVALS: 'New patients and events are arriving before the Action phase starts.',
  HUDDLE: 'Look over your patients and the equipment you may need this turn.',
  ACT: 'Plan care for your patients, arrange help and referrals, then press Ready. The turn ends when Control ends it or time runs out, not when you press Ready.',
  TICK_OPEN: 'The game works out what your care achieved and how long each patient has waited, then shows the Review.',
  TICK_CLOSE: 'The game works out what your care achieved and how long each patient has waited, then shows the Review.',
  LOG: 'See what you did, what could not be done, and how your patients changed. There is no time limit. Control starts the next turn.',
  RELEASE: 'Patients you agreed to take arrive, along with new patients and events. Staff and resources are updated before the next Action phase.',
  HANDOVER: 'Hand every patient over aloud to the day team. There is no Action phase this turn.',
  DEBRIEF: 'The shift is over. Compare outcomes and discuss the decisions behind them.',
  ENDED: 'The session has ended. No more turns or actions remain.',
};

/** Explain the current phase and its place in the turn. */
export function PhaseGuide({ phase, handoverTurn }: Pick<ClockView, 'phase' | 'handoverTurn'>): ReactNode {
  const current = phase === 'TICK_CLOSE' ? 'TICK_OPEN' : phase;
  const terminal = phase === 'ENDED' || (phase === 'DEBRIEF' && !handoverTurn);
  const steps: (ClockView['phase'] | 'RELEASE')[] = terminal ? [phase] : handoverTurn
    ? ['HANDOVER', 'DEBRIEF'] : ['ACT', 'LOG', 'RELEASE'];
  return (
    <section className="phase-guide" aria-label="Turn phases" data-testid="phase-guide">
      <ol className="phase-steps" role="list">
        {steps.map((step, i) => (
          <li key={step} aria-current={step === current ? 'step' : undefined}>
            <span className="phase-step-number" aria-hidden>{i + 1}</span>
            <span className="phase-step-label">{phaseName(step)}</span>
          </li>
        ))}
      </ol>
      <p className="phase-guide-now"><strong>{phase === 'SETUP' ? 'Before the shift' : `Now: ${phaseName(phase)}`}</strong> · {PHASE_HELP[phase]}</p>
      {!terminal && <Fold title="How the turn works">
        {steps.map((step) => <p key={step}><strong>{phaseName(step)}:</strong> {PHASE_HELP[step]}</p>)}
        {!handoverTurn && <p>Your plan holds nothing until the turn ends. When two players want the same thing, the game settles it in a fixed order, and your own actions keep the order you set. An action that clearly cannot happen costs nothing; care you attempt costs its stated actions even if it does not work. An operation needs anaesthetic cover, and a referral needs the receiving team to agree.</p>}
      </Fold>}
    </section>
  );
}

export function phaseTotal(phase: string, pacing?: { arrivalsMs: number; huddleMs: number; actMs: number; tickMs: number; logMs: number; handoverMs: number }): number | undefined {
  if (!pacing) return undefined;
  return ({ ACT: pacing.actMs, HANDOVER: pacing.handoverMs } as Record<string, number>)[phase];
}

export function useToasts(): [{ id: number; text: string; bad?: boolean }[], (text: string, bad?: boolean) => void] {
  const [list, setList] = useState<{ id: number; text: string; bad?: boolean }[]>([]);
  const push = useCallback((text: string, bad?: boolean) => {
    const id = Math.random();
    setList((l) => [...l.slice(-2), { id, text, bad }]);
    window.setTimeout(() => setList((l) => l.filter((x) => x.id !== id)), 3600);
  }, []);
  return [list, push];
}

/**
 * Toasts sit at the bottom (above the phone's tab bar) so they never cover the
 * shift bar or Control's clock; a tap dismisses one early.
 */
export function Toasts({ list }: { list: { id: number; text: string; bad?: boolean }[] }) {
  const [gone, setGone] = useState<Set<number>>(() => new Set());
  return (
    <div className="toasts" role="status" aria-live="polite">
      {list
        .filter((t) => !gone.has(t.id))
        .map((t) => (
          <button key={t.id} type="button" className={`toast ${t.bad ? 'bad' : ''}`} onClick={() => setGone((g) => new Set(g).add(t.id))} aria-label={`${t.text} (tap to dismiss)`}>
            {t.text}
          </button>
        ))}
    </div>
  );
}

/**
 * Progressive disclosure: one accordion style for everything that is useful
 * but not essential. Native <details>, so keyboard and screen readers work.
 */
export function Fold({ title, hint, open, className, testId, children }: { title: ReactNode; hint?: ReactNode; open?: boolean; className?: string; testId?: string; children: ReactNode }) {
  return (
    <details className={`fold ${className ?? ''}`} open={open} data-testid={testId}>
      <summary>
        <span className="fold-title">{title}</span>
        {hint !== undefined && <span className="fold-hint">{hint}</span>}
      </summary>
      <div className="fold-body">{children}</div>
    </details>
  );
}

/** Something private (the secret objective): hidden until tapped, so neighbours cannot read it. */
export function Secret({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  const [shown, setShown] = useState(false);
  return (
    <div className={`secret ${shown ? 'shown' : ''}`} data-testid={testId}>
      <button type="button" className="btn btn-small btn-quiet secret-toggle" aria-expanded={shown} onClick={() => setShown((x) => !x)}>
        {shown ? 'Hide' : label}
      </button>
      {shown && children}
    </div>
  );
}

/**
 * Decorative illustration from /public/art (generated with OpenArt, see docs/UI-REVIEW.md).
 * Always alt="": no text depends on it, and it hides itself if it fails to load.
 */
export function Art({ name, className, eager }: { name: 'hero' | 'dawn'; className?: string; eager?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      className={className}
      src={`/art/${name}-1600.webp`}
      srcSet={`/art/${name}-800.webp 800w, /art/${name}-1600.webp 1600w`}
      sizes="(max-width: 600px) 100vw, 1600px"
      alt=""
      aria-hidden
      decoding="async"
      loading={eager ? 'eager' : 'lazy'}
      onError={() => setFailed(true)}
    />
  );
}

export function Moon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
      <defs>
        <mask id="moon-cut">
          <rect width="32" height="32" fill="#fff" />
          <circle cx="22" cy="11" r="8.5" fill="#000" />
        </mask>
      </defs>
      <circle cx="18" cy="14" r="9" fill="var(--lamp)" mask="url(#moon-cut)" />
    </svg>
  );
}
